/**
 * LISS Surprise Block — AI-Powered Color Matching
 * Analyzes Instagram profiles to match customers with 2 scrunchie colors
 */

;(() => {
  'use strict'

  // Configuration from Liquid
  const CONFIG = window.LISS_SURPRISE_CONFIG || {}
  
  // Rate limiting
  const STORAGE_KEY = 'liss_surprise_generations'
  const MAX_GENERATIONS = 3
  const VIP_PARAM = 'vip'
  const VIP_CODE = 'liss2026'
  
  // Check if VIP mode (bypass rate limit)
  function isVipMode() {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get(VIP_PARAM) === VIP_CODE
  }
  
  // Get stored data
  function getStoredData() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
    } catch {
      return {}
    }
  }
  
  // Get generation count from localStorage
  function getGenerationCount() {
    return getStoredData().count || 0
  }
  
  // Get past matches from localStorage
  function getPastMatches() {
    return getStoredData().matches || []
  }
  
  // Save a match to history
  function saveMatchToHistory(match) {
    try {
      const data = getStoredData()
      data.count = (data.count || 0) + 1
      data.lastGeneration = new Date().toISOString()
      data.matches = data.matches || []
      data.matches.push({
        username: match.username,
        colors: match.colors,
        reasoning: match.reasoning,
        timestamp: new Date().toISOString()
      })
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    } catch (e) {
      console.error('Failed to save match', e)
    }
  }
  
  // Check if rate limit reached
  function hasReachedLimit() {
    if (isVipMode()) return false
    return getGenerationCount() >= MAX_GENERATIONS
  }
  
  // Loading messages for variety
  const LOADING_MESSAGES = [
    { title: 'Analyzing your vibe...', subtitle: 'Browsing through your gorgeous posts' },
    { title: 'Reading your aesthetic...', subtitle: 'Finding the colors that match you' },
    { title: 'Almost there...', subtitle: 'Our AI is crafting your perfect match' },
    { title: 'Curating your colors...', subtitle: 'This is going to be so good' },
  ]

  // State
  let currentStep = 'input'
  let matchResult = null
  let elements = {}

  // Cache DOM elements
  function cacheElements() {
    elements = {
      block: document.querySelector('[data-surprise-block]'),
      trigger: document.querySelector('[data-surprise-trigger]'),
      summary: document.querySelector('[data-surprise-summary]'),
      overlay: document.querySelector('[data-surprise-overlay]'),
      drawer: document.querySelector('[data-surprise-drawer]'),
      closeBtn: document.querySelector('[data-surprise-close]'),
      cancelBtn: document.querySelector('[data-surprise-cancel]'),
      submitBtn: document.querySelector('[data-surprise-submit]'),
      addToCartBtn: document.querySelector('[data-surprise-add-to-cart]'),
      instagramInput: document.querySelector('[data-instagram-input]'),
      inputError: document.querySelector('[data-input-error]'),
      errorMessage: document.querySelector('[data-error-message]'),
      errorRetry: document.querySelector('[data-error-retry]'),
      limitClose: document.querySelector('[data-limit-close]'),
      pastMatchesContainer: document.querySelector('[data-past-matches]'),
      viewPostcardBtn: document.querySelector('[data-surprise-view-postcard]'),
      shareDownload: document.querySelector('[data-share-download]'),
      shareCopy: document.querySelector('[data-share-copy]'),
      
      // Steps
      steps: {
        input: document.querySelector('[data-step="input"]'),
        loading: document.querySelector('[data-step="loading"]'),
        result: document.querySelector('[data-step="result"]'),
        error: document.querySelector('[data-step="error"]'),
        limit: document.querySelector('[data-step="limit"]'),
      },
      
      // Footers
      footers: {
        input: document.querySelector('[data-footer="input"]'),
        result: document.querySelector('[data-footer="result"]'),
      },
      
      // Loading elements
      loadingTitle: document.querySelector('[data-loading-title]'),
      loadingSubtitle: document.querySelector('[data-loading-subtitle]'),
      
      // Summary elements
      summaryColor1: document.querySelector('[data-summary-color-1]'),
      summaryColor2: document.querySelector('[data-summary-color-2]'),
      summaryColorsText: document.querySelector('[data-summary-colors-text]'),
      
      // Postcard elements
      postcard: document.querySelector('[data-postcard]'),
      postcardFor: document.querySelector('[data-postcard-for]'),
      postcardColor1: document.querySelector('[data-postcard-color-1]'),
      postcardColor2: document.querySelector('[data-postcard-color-2]'),
      postcardReasoning: document.querySelector('[data-postcard-reasoning]'),
      postcardPosts: document.querySelector('[data-postcard-posts]'),
    }
  }

  // Open drawer
  function openDrawer() {
    if (!elements.drawer || !elements.overlay) return
    
    // Reset to input step
    showStep('input')
    
    // Clear input
    if (elements.instagramInput) {
      elements.instagramInput.value = ''
    }
    hideError()
    
    // Show drawer
    elements.overlay.removeAttribute('hidden')
    elements.drawer.removeAttribute('hidden')
    
    // Trigger reflow for animation
    elements.overlay.offsetHeight
    elements.drawer.offsetHeight
    
    elements.overlay.classList.add('is-visible')
    elements.drawer.classList.add('is-visible')
    
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', handleEscapeKey)
    
    // Focus input
    setTimeout(() => elements.instagramInput?.focus(), 100)
  }

  // Close drawer
  function closeDrawer() {
    if (!elements.drawer || !elements.overlay) return
    
    elements.overlay.classList.remove('is-visible')
    elements.drawer.classList.remove('is-visible')
    
    setTimeout(() => {
      elements.overlay.setAttribute('hidden', '')
      elements.drawer.setAttribute('hidden', '')
      document.body.style.overflow = ''
      document.removeEventListener('keydown', handleEscapeKey)
    }, 300)
  }

  function handleEscapeKey(e) {
    if (e.key === 'Escape') closeDrawer()
  }

  // Show step
  function showStep(step) {
    currentStep = step
    
    // Hide all steps
    Object.values(elements.steps).forEach(el => {
      if (el) el.setAttribute('hidden', '')
    })
    
    // Show current step
    if (elements.steps[step]) {
      elements.steps[step].removeAttribute('hidden')
    }
    
    // Update footers
    Object.entries(elements.footers).forEach(([key, el]) => {
      if (el) {
        if (key === step) {
          el.removeAttribute('hidden')
        } else {
          el.setAttribute('hidden', '')
        }
      }
    })
  }

  // Show/hide error
  function showError(message) {
    if (elements.inputError) {
      elements.inputError.textContent = message || 'Please enter a valid Instagram username'
      elements.inputError.removeAttribute('hidden')
    }
  }

  function hideError() {
    if (elements.inputError) {
      elements.inputError.setAttribute('hidden', '')
    }
  }

  // Validate username
  function validateUsername(username) {
    // Remove @ if present
    const clean = username.replace(/^@/, '').trim()
    // Basic validation: alphanumeric, underscores, periods, 1-30 chars
    const isValid = /^[a-zA-Z0-9_.]{1,30}$/.test(clean)
    return isValid ? clean : null
  }

  // Cycle loading messages
  let loadingInterval = null
  function startLoadingMessages() {
    let index = 0
    
    function updateMessage() {
      const msg = LOADING_MESSAGES[index % LOADING_MESSAGES.length]
      if (elements.loadingTitle) elements.loadingTitle.textContent = msg.title
      if (elements.loadingSubtitle) elements.loadingSubtitle.textContent = msg.subtitle
      index++
    }
    
    updateMessage()
    loadingInterval = setInterval(updateMessage, 3000)
  }

  function stopLoadingMessages() {
    if (loadingInterval) {
      clearInterval(loadingInterval)
      loadingInterval = null
    }
  }

  // Show past matches in the limit step
  function showPastMatches() {
    const matches = getPastMatches()
    if (!elements.pastMatchesContainer || matches.length === 0) return
    
    elements.pastMatchesContainer.innerHTML = ''
    
    matches.forEach((match, index) => {
      const [color1, color2] = match.colors
      const colorData1 = CONFIG.colors?.[color1] || {}
      const colorData2 = CONFIG.colors?.[color2] || {}
      
      const matchEl = document.createElement('div')
      matchEl.className = 'liss-surprise-past-match'
      matchEl.innerHTML = `
        <div class="liss-surprise-past-match-colors">
          <span class="liss-surprise-past-match-chip" style="background-color: ${colorData1.hex || '#ccc'}"></span>
          <span class="liss-surprise-past-match-chip" style="background-color: ${colorData2.hex || '#ccc'}"></span>
        </div>
        <div class="liss-surprise-past-match-info">
          <span class="liss-surprise-past-match-name">${color1} & ${color2}</span>
          <span class="liss-surprise-past-match-user">@${match.username}</span>
        </div>
        <button type="button" class="liss-surprise-past-match-select" data-select-match="${index}">
          Select
        </button>
      `
      elements.pastMatchesContainer.appendChild(matchEl)
    })
    
    // Bind click events for selecting past matches
    elements.pastMatchesContainer.querySelectorAll('[data-select-match]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const index = parseInt(e.target.dataset.selectMatch, 10)
        const match = matches[index]
        if (match) {
          matchResult = {
            username: match.username,
            colors: match.colors,
            reasoning: match.reasoning || '',
            inspirationPosts: [],
            postcardId: null,
          }
          showResult()
        }
      })
    })
  }

  // Submit for matching
  async function handleSubmit() {
    // Check rate limit first
    if (hasReachedLimit()) {
      showPastMatches()
      showStep('limit')
      return
    }
    
    const rawUsername = elements.instagramInput?.value || ''
    const username = validateUsername(rawUsername)
    
    if (!username) {
      showError('Please enter a valid Instagram username')
      return
    }
    
    hideError()
    showStep('loading')
    startLoadingMessages()
    
    try {
      const response = await fetch(CONFIG.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ instagram_username: username }),
      })
      
      const result = await response.json()
      
      // Check for errors (API returns custom messages)
      if (!response.ok || result.error) {
        throw new Error(result.error || `Something went wrong. Please try again.`)
      }
      
      matchResult = {
        username: username,
        colors: result.matched_colors || [],
        reasoning: result.reasoning || '',
        inspirationPosts: result.inspiration_posts || [],
        postcardId: result.postcard_id || null,
      }
      
      // Save match to history
      saveMatchToHistory(matchResult)
      
      stopLoadingMessages()
      showResult()
      
    } catch (error) {
      console.error('LISS Surprise: API error', error)
      stopLoadingMessages()
      showErrorStep(error.message || 'Something went wrong. Please try again.')
    }
  }

  // Show result
  function showResult() {
    if (!matchResult) return
    
    const { username, colors, reasoning, inspirationPosts } = matchResult
    const [color1, color2] = colors
    const colorData1 = CONFIG.colors?.[color1] || {}
    const colorData2 = CONFIG.colors?.[color2] || {}
    
    console.log('LISS Surprise: Showing result', { 
      color1, color2, 
      colorData1, colorData2,
      configColors: CONFIG.colors
    })
    
    // Update postcard
    if (elements.postcardFor) {
      elements.postcardFor.textContent = `@${username}`
    }
    
    // Color 1
    if (elements.postcardColor1) {
      const imageEl = elements.postcardColor1.querySelector('.liss-surprise-postcard-color-image')
      const nameEl = elements.postcardColor1.querySelector('.liss-surprise-postcard-color-name')
      if (imageEl) {
        imageEl.style.backgroundImage = colorData1.image 
          ? `url(${colorData1.image})` 
          : `linear-gradient(135deg, ${colorData1.hex || '#ccc'}, ${colorData1.hex || '#ccc'})`
        imageEl.style.backgroundColor = colorData1.hex || '#ccc'
      }
      if (nameEl) nameEl.textContent = color1
    }
    
    // Color 2
    if (elements.postcardColor2) {
      const imageEl = elements.postcardColor2.querySelector('.liss-surprise-postcard-color-image')
      const nameEl = elements.postcardColor2.querySelector('.liss-surprise-postcard-color-name')
      if (imageEl) {
        imageEl.style.backgroundImage = colorData2.image 
          ? `url(${colorData2.image})` 
          : `linear-gradient(135deg, ${colorData2.hex || '#ccc'}, ${colorData2.hex || '#ccc'})`
        imageEl.style.backgroundColor = colorData2.hex || '#ccc'
      }
      if (nameEl) nameEl.textContent = color2
    }
    
    // Reasoning (HTML formatted from GPT)
    if (elements.postcardReasoning) {
      elements.postcardReasoning.innerHTML = reasoning
    }
    
    // Inspiration posts - now returned as base64 from API (no proxy needed)
    if (elements.postcardPosts) {
      elements.postcardPosts.innerHTML = ''
      inspirationPosts.slice(0, 3).forEach(post => {
        const postEl = document.createElement('div')
        postEl.className = 'liss-surprise-postcard-post'
        const imageUrl = post.thumbnail || post.url
        if (imageUrl) {
          postEl.style.backgroundImage = `url(${imageUrl})`
        }
        elements.postcardPosts.appendChild(postEl)
      })
    }
    
    // Update summary (for when drawer closes)
    updateSummary()
    
    showStep('result')
  }

  // Update block summary
  function updateSummary() {
    if (!matchResult) return
    
    const { colors } = matchResult
    const [color1, color2] = colors
    const colorData1 = CONFIG.colors?.[color1] || {}
    const colorData2 = CONFIG.colors?.[color2] || {}
    
    if (elements.summaryColor1) {
      elements.summaryColor1.style.backgroundColor = colorData1.hex || '#ccc'
    }
    if (elements.summaryColor2) {
      elements.summaryColor2.style.backgroundColor = colorData2.hex || '#ccc'
    }
    if (elements.summaryColorsText) {
      elements.summaryColorsText.textContent = `${color1} & ${color2} — matched just for you`
    }
    
    // Show summary, hide trigger
    if (elements.trigger) elements.trigger.setAttribute('hidden', '')
    if (elements.summary) elements.summary.removeAttribute('hidden')
  }

  // Show error step
  function showErrorStep(message) {
    if (elements.errorMessage) {
      elements.errorMessage.textContent = message
    }
    showStep('error')
  }

  // Add to cart
  let isAddingToCart = false
  async function handleAddToCart() {
    console.log('LISS Surprise: Add to cart clicked', { matchResult, variantId: CONFIG.variantId })
    
    if (!matchResult) {
      console.error('LISS Surprise: No match result')
      alert('Please complete the matching process first.')
      return
    }
    
    if (!CONFIG.variantId) {
      console.error('LISS Surprise: No variant ID configured')
      alert('Product configuration error. Please refresh and try again.')
      return
    }
    
    if (isAddingToCart) return // Prevent double-clicks
    
    isAddingToCart = true
    if (elements.addToCartBtn) elements.addToCartBtn.disabled = true
    
    const { colors, username, postcardId } = matchResult
    const [color1, color2] = colors
    
    const formData = new FormData()
    formData.append('id', String(CONFIG.variantId))
    formData.append('quantity', '1')
    
    // Add color properties (prefixed with _ to hide from customer but show in back office)
    formData.append('properties[_Color 1]', color1)
    formData.append('properties[_Color 2]', color2)
    formData.append('properties[_Instagram]', `@${username}`)
    if (postcardId) {
      formData.append('properties[_Postcard ID]', postcardId)
    }
    
    // Visible property for customer
    formData.append('properties[Your Colors]', `${color1} & ${color2}`)
    
    // Get cart section IDs for drawer updates
    const cartItemsComponents = document.querySelectorAll('cart-items-component')
    const sectionIds = []
    cartItemsComponents.forEach(item => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId)
      }
    })
    
    if (sectionIds.length > 0) {
      formData.append('sections', sectionIds.join(','))
      formData.append('sections_url', window.location.pathname)
    }
    
    try {
      console.log('LISS Surprise: Adding to cart with variant ID:', CONFIG.variantId)
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      })
      
      const result = await response.json()
      console.log('LISS Surprise: Cart response:', result)
      
      if (!response.ok) {
        console.error('LISS Surprise: Cart error response:', result)
        throw new Error(result.description || result.message || 'Failed to add to cart')
      }
      
      // Refresh cart by fetching updated cart
      const cartResponse = await fetch('/cart.js')
      const cartData = await cartResponse.json()
      console.log('LISS Surprise: Cart now has', cartData.item_count, 'items')
      
      // Dispatch custom event for theme cart updates
      window.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }))
      
      // Also try standard Shopify cart events
      document.dispatchEvent(new CustomEvent('cart:item-added', {
        detail: { 
          item: result,
          cart: cartData
        }
      }))
      
      // Close our drawer and go to cart
      closeDrawer()
      
      console.log('LISS Surprise: Added to cart successfully', { colors, username, cartItems: cartData.item_count })
      
      // Redirect to cart page (most reliable across all themes)
      window.location.href = '/cart'
      
    } catch (error) {
      console.error('LISS Surprise: Failed to add to cart', error)
      alert('Failed to add to cart. Please try again.')
    } finally {
      isAddingToCart = false
      if (elements.addToCartBtn) elements.addToCartBtn.disabled = false
    }
  }

  // Download postcard as image
  async function handleDownloadPostcard() {
    // Use html2canvas if available, otherwise fallback to simple screenshot hint
    if (typeof html2canvas !== 'undefined' && elements.postcard) {
      try {
        const canvas = await html2canvas(elements.postcard, {
          scale: 2,
          backgroundColor: '#fdfcfa',
          useCORS: true,
        })
        
        const link = document.createElement('a')
        link.download = `liss-surprise-${matchResult?.username || 'postcard'}.png`
        link.href = canvas.toDataURL('image/png')
        link.click()
      } catch (error) {
        console.error('Failed to generate postcard image', error)
        alert('Take a screenshot to save your postcard!')
      }
    } else {
      alert('Take a screenshot to save your postcard!')
    }
  }

  // Copy shareable message
  function handleCopyLink() {
    if (!matchResult) {
      alert('No match to share yet!')
      return
    }
    
    const { username, colors } = matchResult
    const [color1, color2] = colors
    const productUrl = `${window.location.origin}/products/silk-scrunchies-surprise-set`
    const message = `LISS AI matched me with ${color1} & ${color2} scrunchies based on my Instagram vibe! ✨\n\nGet your personalized match: ${productUrl}`
    
    navigator.clipboard.writeText(message).then(() => {
      const btn = elements.shareCopy
      if (btn) {
        const originalText = btn.innerHTML
        btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="18" height="18"><path d="M20 6L9 17l-5-5"/></svg> Copied!'
        setTimeout(() => btn.innerHTML = originalText, 2000)
      }
    }).catch(() => {
      // Fallback for older browsers
      alert('Copy this message:\n\n' + message)
    })
  }

  // Bind events
  function bindEvents() {
    // Trigger click
    elements.trigger?.addEventListener('click', () => {
      if (matchResult) {
        // Already have a result, just open to show it
        openDrawer()
        showStep('result')
      } else {
        openDrawer()
      }
    })
    
    // View postcard from summary
    elements.viewPostcardBtn?.addEventListener('click', () => {
      openDrawer()
      showStep('result')
    })
    
    // Overlay click
    elements.overlay?.addEventListener('click', closeDrawer)
    
    // Close button
    elements.closeBtn?.addEventListener('click', closeDrawer)
    
    // Cancel button
    elements.cancelBtn?.addEventListener('click', closeDrawer)
    
    // Submit button
    elements.submitBtn?.addEventListener('click', handleSubmit)
    
    // Enter key on input
    elements.instagramInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleSubmit()
      }
    })
    
    // Input change - hide error
    elements.instagramInput?.addEventListener('input', hideError)
    
    // Error retry
    elements.errorRetry?.addEventListener('click', () => {
      showStep('input')
      elements.instagramInput?.focus()
    })
    
    // Limit close
    elements.limitClose?.addEventListener('click', closeDrawer)
    
    // Add to cart
    elements.addToCartBtn?.addEventListener('click', handleAddToCart)
    
    // Share buttons
    elements.shareDownload?.addEventListener('click', handleDownloadPostcard)
    elements.shareCopy?.addEventListener('click', handleCopyLink)
  }

  // Initialize
  function init() {
    cacheElements()
    
    if (!elements.block) {
      console.log('LISS Surprise: Block not found on this page')
      return
    }
    
    bindEvents()
    console.log('LISS Surprise: Initialized', CONFIG)
  }

  // Run on DOMContentLoaded or immediately if already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }

  // Public API
  window.LissSurprise = {
    open: openDrawer,
    close: closeDrawer,
    getResult: () => matchResult ? { ...matchResult } : null,
  }
})()
